'use client';

import { classNames } from '@lib/classNames/classNames';
import { useMemo } from 'react';

import { Button } from '../button';
import { ScrollButton } from '../scrollButton';
import { CTAButtonsProps } from './types';

import { useUtmHref } from '@/shared/hooks';
import { sendAnalyticsEvent } from '@/shared/lib/analytics/analytics';

export const CTAButtons = ({
  mainButton,
  secondaryButton,
}: CTAButtonsProps) => {
  const mainButtonHref = useUtmHref(mainButton?.href);
  const secondaryButtonHref = useUtmHref(secondaryButton?.href);

  const mainButtonElement = useMemo(() => {
    if (!mainButton || !mainButton.label) {
      return null;
    }

    const handleButtonClick = () => {
      if (mainButton.analyticsEvent) {
        sendAnalyticsEvent(mainButton.analyticsEvent);
      }
    };


    const className = classNames({
      cls: ' w-fit items-center justify-center typo-text-base max-md:w-full text-nowrap',
      additional: [mainButton.className],
    });

    if (mainButtonHref) {
      return (
        <Button
          className={className}
          variant={mainButton.variant ?? 'primary'}
          disabled={false}
          role="button"
          href={mainButtonHref}
          target={mainButton.target}
          onClick={handleButtonClick}
          spacing={mainButton.spacing}
        >
          {mainButton.label}
        </Button>
      );
    }

    return (
      <ScrollButton
        target="form"
        className={className}
        variant={mainButton.variant ?? 'primary'}
        spacing={mainButton.spacing}
      >
        {mainButton.label}
      </ScrollButton>
    );
  }, [mainButton, mainButtonHref]);

  const secondaryButtonElement = useMemo(() => {
    if (!secondaryButton || !secondaryButton.label) {
      return null;
    }

    const handleButtonClick = () => {
      if (secondaryButton?.analyticsEvent) {
        sendAnalyticsEvent(secondaryButton?.analyticsEvent);
      }
    };


    const className = classNames({
      cls: 'w-fit items-center justify-center typo-text-base backdrop-blur-[20px] max-md:w-full text-nowrap',
      additional: [secondaryButton.className],
    });

    if (secondaryButtonHref) {
      return (
        <Button
          className={className}
          variant={secondaryButton.variant ?? 'secondary'}
          role="button"
          href={secondaryButtonHref}
          onClick={handleButtonClick}
          target={secondaryButton.target}
          spacing={secondaryButton.spacing}
        >
          {secondaryButton.label}
        </Button>
      );
    }

    return (
      <ScrollButton
        target="form"
        className={className}
        variant={secondaryButton.variant ?? 'secondary'}
        spacing={secondaryButton.spacing}
      >
        {secondaryButton.label}
      </ScrollButton>
    );
  }, [secondaryButton, secondaryButtonHref]);

  return (
    <>
      {secondaryButtonElement}
      {mainButtonElement}
    </>
  );
};
